/**
 * Bulk Actions API - Perform bulk operations on contacts, deals, companies
 */
import apiClient from './index'

export interface BulkActionResult {
  updated_count?: number
  deleted_count?: number
  total_requested: number
  errors?: Array<{ id: string; error: string }>
  assigned_to?: string
  new_stage?: string
  tags_added?: string[]
  tags_removed?: string[]
}

export interface BulkActionResponse {
  data: BulkActionResult
}

// ============== Contact Bulk Actions ==============

/**
 * Bulk update contacts
 */
export async function bulkUpdateContacts(
  contactIds: string[],
  updates: Record<string, any>
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/contacts/update', {
    contact_ids: contactIds,
    updates,
  })
  return response.data.data
}

/**
 * Bulk delete contacts
 */
export async function bulkDeleteContacts(
  contactIds: string[]
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/contacts/delete', {
    contact_ids: contactIds,
  })
  return response.data.data
}

/**
 * Bulk add tags to contacts
 */
export async function bulkAddTagsToContacts(
  contactIds: string[],
  tags: string[]
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/contacts/add-tags', {
    contact_ids: contactIds,
    tags,
  })
  return response.data.data
}

/**
 * Bulk remove tags from contacts
 */
export async function bulkRemoveTagsFromContacts(
  contactIds: string[],
  tags: string[]
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/contacts/remove-tags', {
    contact_ids: contactIds,
    tags,
  })
  return response.data.data
}

/**
 * Bulk assign contacts to a user
 */
export async function bulkAssignContacts(
  contactIds: string[],
  assignToUserId: string
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/contacts/assign', {
    contact_ids: contactIds,
    assign_to_user_id: assignToUserId,
  })
  return response.data.data
}

// ============== Deal Bulk Actions ==============

/**
 * Bulk update deals
 */
export async function bulkUpdateDeals(
  dealIds: string[],
  updates: Record<string, any>
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/deals/update', {
    deal_ids: dealIds,
    updates,
  })
  return response.data.data
}

/**
 * Bulk change deal stage
 */
export async function bulkChangeDealStage(
  dealIds: string[],
  stage: string
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/deals/stage', {
    deal_ids: dealIds,
    stage,
  })
  return response.data.data
}

/**
 * Bulk assign deals to a user
 */
export async function bulkAssignDeals(
  dealIds: string[],
  assignToUserId: string
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/deals/assign', {
    deal_ids: dealIds,
    assign_to_user_id: assignToUserId,
  })
  return response.data.data
}

/**
 * Bulk delete deals
 */
export async function bulkDeleteDeals(
  dealIds: string[]
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/deals/delete', {
    deal_ids: dealIds,
  })
  return response.data.data
}

// ============== Company Bulk Actions ==============

/**
 * Bulk update companies
 */
export async function bulkUpdateCompanies(
  companyIds: string[],
  updates: Record<string, any>
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/companies/update', {
    company_ids: companyIds,
    updates,
  })
  return response.data.data
}

/**
 * Bulk assign companies to a user
 */
export async function bulkAssignCompanies(
  companyIds: string[],
  assignToUserId: string
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/companies/assign', {
    company_ids: companyIds,
    assign_to_user_id: assignToUserId,
  })
  return response.data.data
}

/**
 * Bulk delete companies
 */
export async function bulkDeleteCompanies(
  companyIds: string[]
): Promise<BulkActionResult> {
  const response = await apiClient.post<BulkActionResponse>('/bulk/companies/delete', {
    company_ids: companyIds,
  })
  return response.data.data
}
